/**
 * How often an open tab asks whether a new build has been deployed.
 *
 * This used to be an hour, which is how long a terminal could keep running the
 * previous release: the SPA never navigates, so nothing else re-fetches
 * `sw.js`. Fifteen minutes is the ceiling rather than the normal case — a tab
 * coming back into view checks immediately (see the resume handler below), and
 * `/api/ping` reporting a new deployment asks for a check the moment it notices.
 */
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;
/** Floor between resume-triggered checks, so alt-tabbing does not poll. */
const RESUME_CHECK_MIN_GAP_MS = 60 * 1000;

let swRegistration = null;
let lastUpdateCheckAt = 0;
let reloading = false;

/**
 * Whether a new build has taken over and this tab is on its way to reloading.
 *
 * Module scope rather than a local, because the offline caches need the answer:
 * a reset performed in the seconds before a reload would be carried out by the
 * *outgoing* bundle, and its stamp would then make the incoming one believe the
 * work was already done with the right normalizers.
 */
export function isReloadPending() {
    return reloading;
}

export function registerSW() {
    if (!('serviceWorker' in navigator)) return;

    const swUrl = import.meta.env.MODE === 'production' ? '/sw.js' : '/dev-sw.js?dev-sw';

    /**
     * Whether this page was already under a service worker's control.
     *
     * `controllerchange` fires on a *first* install too, because `clientsClaim()`
     * takes control of a page that had none. Reloading on that edge meant every
     * first visit — and every visit after a cache clear — reloaded itself for no
     * reason. Only a controller *replacing* another controller is a new build.
     */
    const hadController = Boolean(navigator.serviceWorker.controller);

    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController) return;
        // controllerchange can fire more than once; a second reload while the
        // first is already navigating is how a refresh loop starts.
        if (reloading) return;
        reloading = true;
        // The forceReload argument has been ignored by every browser for years.
        window.location.reload();
    });

    const register = () => {
        navigator.serviceWorker
            .register(swUrl, { scope: '/' })
            .then((registration) => {
                swRegistration = registration;
                // Registering an *already registered* worker does not itself go
                // to the network for a byte comparison in every browser, and a
                // tab that was open across the deploy has no navigation coming
                // to force one. Ask once up front so a reload is all it takes.
                checkForAppUpdate();
                setInterval(() => checkForAppUpdate(), UPDATE_CHECK_INTERVAL_MS);
            })
            .catch((error) => {
                console.error('SW registration failed:', error);
            });
    };

    /**
     * A tab returning to the foreground is the cheapest moment to notice a
     * release: nobody is mid-keystroke, and the reload that may follow costs a
     * screen the operator was not looking at a second ago.
     */
    const onResume = () => {
        if (typeof document !== 'undefined' && document.hidden) return;
        if (Date.now() - lastUpdateCheckAt < RESUME_CHECK_MIN_GAP_MS) return;
        checkForAppUpdate();
    };

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onResume);
    }
    window.addEventListener('focus', onResume);

    // `load` has usually already fired by the time bootstrap() reaches here —
    // it runs after `await router.isReady()` and `app.mount()`. Waiting for an
    // event that has been and gone meant the service worker was never
    // registered at all on a warm start, so the app had no offline shell.
    if (document.readyState === 'complete') {
        register();
    } else {
        window.addEventListener('load', register, { once: true });
    }
}

/**
 * Ask the browser to re-fetch `sw.js` and report whether a new build is coming.
 *
 * The return value is the useful part, and it is what lets a deployment be
 * handled correctly in both of its shapes. `true` means a replacement worker is
 * installing or waiting, so `controllerchange` is about to reload this tab and
 * whatever else the caller was going to do belongs *after* that reload — the
 * outgoing bundle should not be the one to rebuild the offline caches. `false`
 * means the assets this tab is running are already the deployed ones, so a
 * server-side-only release (a migration, a changed payload) has to be handled
 * here and now, because no reload is coming.
 */
export async function checkForAppUpdate() {
    if (!('serviceWorker' in navigator)) return false;
    if (reloading) return true;
    lastUpdateCheckAt = Date.now();

    try {
        const registration = swRegistration ?? (await navigator.serviceWorker.getRegistration('/'));
        if (!registration) return false;
        swRegistration = registration;

        // `updatefound` is the reliable witness, and the reason the states below
        // are not enough on their own: the worker ships with `skipWaiting` and
        // `clientsClaim`, so a replacement can install, activate and claim this
        // page while `update()` is still settling — leaving `installing` and
        // `waiting` both null on a registration that just changed hands. Reading
        // only those states reported "no update" for the fastest updates there
        // are, which are exactly the ones a reload is already coming for.
        let updateFound = false;
        const onUpdateFound = () => {
            updateFound = true;
        };
        registration.addEventListener('updatefound', onUpdateFound);
        try {
            await registration.update();
        } finally {
            registration.removeEventListener('updatefound', onUpdateFound);
        }

        // `skipWaiting` normally means nothing ever waits. Nudging one that does
        // costs nothing and covers a build where that option was turned off.
        if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            return true;
        }

        return updateFound || reloading || Boolean(registration.installing);
    } catch (error) {
        console.warn('[serviceWorker] update check failed', error);
        return false;
    }
}
